import ExpandMore from "@mui/icons-material/ChevronRight";
import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useTheme,
} from "@mui/material";
import { kebabCase } from "lodash-es";
import { type ElementType, type MouseEvent, useCallback, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";

import { useLocalStorageState } from "../../hooks/useLocalStorageState";
import { useAlerts } from "../Alerts/AlertProvider";

type BaseLinkNavItem = {
  text: string;
  description?: string;
  path: string;
  isDisabled?: boolean;
  textColor?: string;
  isExternal?: boolean;
  matchPaths?: string[];
};

export type StandardNavItem = BaseLinkNavItem & {
  kind: "Standard";
  Icon: ElementType;
};

export type GroupChildNavItem = BaseLinkNavItem & {
  kind: "GroupChild";
};

export type GroupNavItem = {
  kind: "Group";
  text: string;
  description?: string;
  Icon: ElementType;
  path: string;
  isExpandable?: boolean;
  children: GroupChildNavItem[];
};

type NavItemProps = {
  navItem: StandardNavItem | GroupNavItem | GroupChildNavItem;
  collapsed: boolean;
  onNavigate?: () => void;
};

const NAV_ITEM_BORDER_RADIUS = "8px";

function getInternalUrl(path: string | undefined, text: string) {
  const resolvedPath = path ?? text.split("/").map(kebabCase).join("/");
  return `/${resolvedPath}`;
}

function getMatchPaths(path: string | undefined, text: string, matchPaths: string[] = []) {
  return [
    getInternalUrl(path, text),
    ...matchPaths.map((matchPath) => getInternalUrl(matchPath, text)),
  ];
}

function isPathMatch(currentPath: string, candidatePath: string) {
  return currentPath === candidatePath || currentPath.startsWith(`${candidatePath}/`);
}

function isNavItemActive(
  currentPath: string,
  item: Pick<BaseLinkNavItem, "path" | "text" | "matchPaths" | "isExternal">
) {
  if (item.isExternal) {
    return false;
  }

  return getMatchPaths(item.path, item.text, item.matchPaths).some((candidatePath) =>
    isPathMatch(currentPath, candidatePath)
  );
}

function renderIconColor(isActive: boolean, textColor?: string) {
  if (textColor) {
    return textColor;
  }

  if (isActive) {
    return "white";
  }

  return "#878D99";
}

function renderLinkTextColor(isActive: boolean, textColor?: string) {
  if (textColor) {
    return textColor;
  }

  if (isActive) {
    return "white";
  }

  return "white";
}

function getNavItemUrl(navItem: BaseLinkNavItem) {
  if (navItem.isExternal) {
    return navItem.path ?? "";
  }

  return getInternalUrl(navItem.path, navItem.text);
}

function getNavItemTooltipTitle(navItem: Pick<BaseLinkNavItem, "text" | "isExternal">) {
  return navItem.isExternal ? `${navItem.text} (opens in new tab)` : navItem.text;
}

function getGroupNavItemUrl(navItem: GroupNavItem) {
  if (!navItem.isExpandable) {
    return getInternalUrl(navItem.path, navItem.text);
  }

  const firstChild = navItem.children[0];
  return firstChild ? getNavItemUrl(firstChild) : getInternalUrl(navItem.path, navItem.text);
}

function handleDisabledNavItemClick(
  event: MouseEvent<HTMLAnchorElement>,
  navItem: Pick<BaseLinkNavItem, "isDisabled">,
  alerts: ReturnType<typeof useAlerts>
) {
  if (navItem.isDisabled) {
    event.preventDefault();
    alerts.info("This section is not yet available.");
  }
}

function StandardNavLink({
  navItem,
  collapsed,
  onNavigate,
}: {
  navItem: StandardNavItem;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const alerts = useAlerts();
  const location = useLocation();
  const isActive = isNavItemActive(location.pathname, navItem);
  const iconColor = renderIconColor(isActive, navItem.textColor);
  const url = getNavItemUrl(navItem);
  const tooltipTitle = getNavItemTooltipTitle(navItem);
  const theme = useTheme();

  if (collapsed) {
    return (
      <ListItem
        disablePadding
        sx={{
          display: "flex",
          justifyContent: "center",
          px: 1.5,
          py: 0.5,
        }}
      >
        <Tooltip
          title={tooltipTitle}
          placement="right"
          enterDelay={100}
          disableInteractive
          slotProps={{
            tooltip: {
              sx: {
                pointerEvents: "none",
              },
            },
          }}
        >
          <NavLink
            aria-label={tooltipTitle}
            onClick={(event) => {
              handleDisabledNavItemClick(event, navItem, alerts);
              if (!navItem.isDisabled) {
                onNavigate?.();
              }
            }}
            to={url}
            target={navItem.isExternal ? "_blank" : undefined}
            rel={navItem.isExternal ? "noopener noreferrer" : undefined}
            style={{
              textDecoration: "none",
              color: "inherit",
              display: "block",
              width: "100%",
            }}
          >
            <ListItemButton
              sx={{
                minHeight: 44,
                minWidth: 44,
                justifyContent: "center",
                px: 0,
                py: 1,
                borderRadius: 999,
                backgroundColor: isActive ? theme.palette.encampBlue.main : "transparent",
                "&:hover": {
                  backgroundColor: isActive ? "#4A88D0" : "transparent",
                  ".MuiSvgIcon-root": {
                    color: isActive ? "white" : "#4A88D0",
                  },
                },
              }}
            >
              <navItem.Icon
                sx={{
                  fontSize: "24px",
                  color: iconColor,
                  opacity: navItem.textColor && !isActive ? 0.5 : 1,
                }}
              />
            </ListItemButton>
          </NavLink>
        </Tooltip>
      </ListItem>
    );
  }

  return (
    <ListItem disablePadding>
      <NavLink
        aria-label={navItem.text}
        onClick={(event) => {
          handleDisabledNavItemClick(event, navItem, alerts);
          if (!navItem.isDisabled) {
            onNavigate?.();
          }
        }}
        to={url}
        target={navItem.isExternal ? "_blank" : undefined}
        rel={navItem.isExternal ? "noopener noreferrer" : undefined}
        style={{
          textDecoration: "none",
          color: "inherit",
          width: "100%",
          marginLeft: "8px",
          marginRight: "8px",
          display: "flex",
          justifyContent: "center",
          flexDirection: "row",
        }}
      >
        <ListItemButton
          sx={{
            my: theme.spacing(1),
            paddingY: 0,
            background: isActive ? "#4A88D0" : "none",
            pl: "16px",
            pr: 2,
            borderRadius: NAV_ITEM_BORDER_RADIUS,
            "&:hover": {
              backgroundColor: isActive ? "#4A88D0" : "transparent",
              ".MuiSvgIcon-root": {
                color: isActive ? "white" : "#4A88D0",
              },
              ".MuiTypography-root": {
                color: isActive ? "white" : "#4A88D0",
              },
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 24,
              mr: "12px",
            }}
          >
            <navItem.Icon
              sx={{
                fontSize: "24px",
                color: iconColor,
                opacity: navItem.textColor && !isActive ? 0.5 : 1,
              }}
            />
          </ListItemIcon>
          <ListItemText
            primary={navItem.text}
            slotProps={{
              primary: { sx: { color: renderLinkTextColor(isActive, navItem.textColor) } },
            }}
          />
        </ListItemButton>
      </NavLink>
    </ListItem>
  );
}

function GroupChildNavLink({
  navItem,
  onNavigate,
}: {
  navItem: GroupChildNavItem;
  onNavigate?: () => void;
}) {
  const alerts = useAlerts();
  const location = useLocation();
  const isActive = isNavItemActive(location.pathname, navItem);
  const url = getNavItemUrl(navItem);

  const selectedHeight = "32px";

  return (
    <ListItem
      disablePadding
      sx={{
        display: "flex",
        height: "40px",
        px: "8px",
        "&:hover": {
          ".MuiTypography-root": {
            color: isActive ? "white" : "#4A88D0",
          },
        },
      }}
    >
      <NavLink
        aria-label={navItem.text}
        onClick={(event) => {
          handleDisabledNavItemClick(event, navItem, alerts);
          if (!navItem.isDisabled) {
            onNavigate?.();
          }
        }}
        to={url}
        target={navItem.isExternal ? "_blank" : undefined}
        rel={navItem.isExternal ? "noopener noreferrer" : undefined}
        style={{
          textDecoration: "none",
          color: "inherit",
          width: "100%",
          display: "contents",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "28px",
            height: "100%",
            p: 0,
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: selectedHeight,

              borderTopLeftRadius: NAV_ITEM_BORDER_RADIUS,
              borderBottomLeftRadius: NAV_ITEM_BORDER_RADIUS,
              background: isActive ? "#4A88D0" : "none",
            }}
          />
        </Box>
        <Box
          aria-hidden
          sx={{
            width: "1px",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: "100%",
              flex: 1,
              backgroundColor: "#596376",
            }}
          />
          <Box
            sx={{
              width: "100%",
              height: selectedHeight,
              flexShrink: 0,
              backgroundColor: isActive ? "white" : "#596376",
            }}
          />
          <Box
            sx={{
              width: "100%",
              flex: 1,
              backgroundColor: "#596376",
            }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flex: 1,
            minWidth: 0,

            justifyContent: "center",
            flexDirection: "row",
            height: selectedHeight,
            borderTopRightRadius: NAV_ITEM_BORDER_RADIUS,
            borderBottomRightRadius: NAV_ITEM_BORDER_RADIUS,
            background: isActive ? "#4A88D0" : "none",
          }}
        >
          <ListItemButton
            sx={{
              my: 0,
              py: 0,
              pl: 3,
              pr: 0,
              borderTopRightRadius: NAV_ITEM_BORDER_RADIUS,
              borderBottomRightRadius: NAV_ITEM_BORDER_RADIUS,
            }}
          >
            <ListItemText
              primary={navItem.text}
              primaryTypographyProps={{
                fontSize: "14px",
                lineHeight: "24px",
                color: renderLinkTextColor(isActive, navItem.textColor),
                marginLeft: 0,
              }}
            />
          </ListItemButton>
        </Box>
      </NavLink>
    </ListItem>
  );
}

function GroupNavRow({
  navItem,
  collapsed,
  onNavigate,
}: {
  navItem: GroupNavItem;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const theme = useTheme();
  const location = useLocation();
  const groupUrl = getGroupNavItemUrl(navItem);
  const collapseId = `nav-group-${kebabCase(navItem.text)}`;
  const storageKey = `nav-menu-${navItem.text}-group-expanded`;
  const isExpandable = navItem.isExpandable ?? true;

  const groupIsActive = useMemo(() => {
    if (!isExpandable) {
      return isNavItemActive(location.pathname, {
        path: navItem.path,
        text: navItem.text,
      });
    }

    return navItem.children.some((child) => isNavItemActive(location.pathname, child));
  }, [isExpandable, location.pathname, navItem.children, navItem.path, navItem.text]);
  const [persistedExpanded, setPersistedExpanded] = useLocalStorageState<boolean>(storageKey, {
    defaultValue: false,
  });
  const [expanded, setExpanded] = useState(persistedExpanded);
  const groupShowsActiveState = useMemo(() => {
    if (!isExpandable) {
      return groupIsActive;
    }

    if (expanded && !collapsed) {
      return false;
    }

    return groupIsActive;
  }, [collapsed, expanded, groupIsActive, isExpandable]);

  const updateExpanded = useCallback(
    (nextExpanded: boolean | ((currentExpanded: boolean) => boolean)) => {
      const resolvedExpanded =
        typeof nextExpanded === "function" ? nextExpanded(expanded) : nextExpanded;

      setExpanded(resolvedExpanded);
      setPersistedExpanded(resolvedExpanded);
    },
    [expanded, setPersistedExpanded]
  );

  if (collapsed) {
    const tooltipTitle = navItem.text;

    return (
      <ListItem
        disablePadding
        sx={{
          display: "flex",
          justifyContent: "center",
          px: 1.5,
          py: 0.5,
        }}
      >
        <Tooltip
          title={tooltipTitle}
          placement="right"
          enterDelay={100}
          disableInteractive
          slotProps={{
            tooltip: {
              sx: {
                pointerEvents: "none",
              },
            },
          }}
        >
          <NavLink
            aria-label={tooltipTitle}
            onClick={() => {
              onNavigate?.();
            }}
            to={groupUrl}
            style={{
              textDecoration: "none",
              color: "inherit",
              display: "block",
              width: "100%",
            }}
          >
            <ListItemButton
              selected={groupShowsActiveState}
              sx={{
                minHeight: 44,
                minWidth: 44,
                justifyContent: "center",
                px: 0,
                py: 1,
                borderRadius: 999,
                "&.Mui-selected": {
                  backgroundColor: theme.palette.encampBlue.main,
                },
                "&.Mui-selected:hover": {
                  backgroundColor: "#4A88D0",
                },
                "&:hover": {
                  backgroundColor: groupShowsActiveState ? "#4A88D0" : "none",
                  ".MuiSvgIcon-root": {
                    color: groupShowsActiveState ? "white" : "#4A88D0",
                  },
                },
              }}
            >
              <navItem.Icon
                sx={{
                  fontSize: "24px",
                  color: renderIconColor(groupShowsActiveState),
                }}
              />
            </ListItemButton>
          </NavLink>
        </Tooltip>
      </ListItem>
    );
  }

  return (
    <ListItem disablePadding sx={{ display: "block" }}>
      <Box
        sx={{
          p: 0,
          m: "8px",
          height: "38px",
          display: "flex",
          alignItems: "center",
          "&:hover": {
            backgroundColor: groupShowsActiveState ? "#4A88D0" : "none",
            ".MuiSvgIcon-root": {
              color: groupShowsActiveState ? "white" : theme.palette.encampBlue.main,
            },
            ".MuiTypography-root": {
              color: groupShowsActiveState ? "white" : theme.palette.encampBlue.main,
            },
          },
          background: groupShowsActiveState ? "#4A88D0" : "none",
          borderRadius: NAV_ITEM_BORDER_RADIUS,
        }}
      >
        <NavLink
          aria-label={navItem.text}
          onClick={() => {
            if (isExpandable) {
              updateExpanded(true);
            }
            onNavigate?.();
          }}
          to={groupUrl}
          style={{
            textDecoration: "none",
            color: "inherit",
            width: "100%",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <ListItemIcon
            sx={{
              pl: "16px",
              minWidth: 24,
              mr: "12px",
            }}
          >
            <navItem.Icon
              sx={{
                color: renderIconColor(groupShowsActiveState),
              }}
            />
          </ListItemIcon>

          <ListItemButton
            sx={{
              p: 0,
              minHeight: "38px",
              minWidth: 0,
              borderRadius: NAV_ITEM_BORDER_RADIUS,
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <ListItemText
              primary={navItem.text}
              slotProps={{
                primary: {
                  sx: {
                    fontSize: "16px",
                    color: "white",
                    ml: 0,
                  },
                },
              }}
            />
          </ListItemButton>

          {isExpandable && (
            <IconButton
              aria-controls={collapseId}
              aria-expanded={expanded}
              aria-label={`${expanded ? "Collapse" : "Expand"} ${navItem.text}`}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                updateExpanded((current) => !current);
              }}
              size="small"
              sx={{
                mr: "4px",
                borderRadius: NAV_ITEM_BORDER_RADIUS,
              }}
            >
              <ExpandMore
                sx={{
                  color: groupShowsActiveState ? "white" : "#8D98A6",
                  transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease-in-out",
                }}
              />
            </IconButton>
          )}
        </NavLink>
      </Box>
      {isExpandable && (
        <Collapse id={collapseId} in={expanded} timeout="auto" unmountOnExit>
          <List disablePadding>
            {navItem.children.map((child) => (
              <GroupChildNavLink
                key={`${navItem.text}-${child.text}`}
                navItem={child}
                onNavigate={onNavigate}
              />
            ))}
          </List>
        </Collapse>
      )}
    </ListItem>
  );
}

export function NavItemV2({ navItem, collapsed, onNavigate }: NavItemProps) {
  if (navItem.kind === "Group") {
    return <GroupNavRow navItem={navItem} collapsed={collapsed} onNavigate={onNavigate} />;
  }

  if (navItem.kind === "Standard") {
    return <StandardNavLink navItem={navItem} collapsed={collapsed} onNavigate={onNavigate} />;
  }

  return <GroupChildNavLink navItem={navItem} onNavigate={onNavigate} />;
}
